import React, { useState, useEffect, useMemo, useContext, useRef, startTransition } from "react";
import {
    CollisionDetection,
    DndContext,
    DragEndEvent,
    DragMoveEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    MeasuringStrategy,
    MouseSensor,
    TouchSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    closestCorners,
    pointerWithin,
} from "@dnd-kit/core";
import {
    SortableContext,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ICustomerItem,
  IKavDataItem,
  IOrderItem,
} from "../../../../modules/API/Drivers/models/kavData";
import { IWorkerItem } from "../../../../modules/API/Defaults/models/workers"
import { useDispatch, useSelector } from "react-redux";
import {
  LOAD,
  LOAD_END,
  SELECT_MARKETING_AREA,
} from "../../../../../store/actions/actionsTypes";
import { RootState } from "../../../../../store/reducers/rootReducer";
import { Defaults } from "../../../../modules/API/Defaults/defaults";
import { ICarItem, TCarDict } from "../../../../../models/Defaults/carList";
import { Trucks } from "../../../../modules/API/Distribution/trucks";
import styled from "styled-components";
import ColumModal from "../../../../generic/Layouts/ColumModal";
import Logger from "./Logger/Logger";
import DatePickerModule from "../../../../generic/datePicker/datePicker";
import SearchInput from "../../../../generic/inputs/inputSearch";
import KavItem from "./Items/KavItem";
import ColumClientElem from "./Items/ColumClientElem";
import {
  halperMergeOrders,
  helperApiDataCollector,
  helperFormatSourceSelectedItemForDrag,
  helperFormatSourceSelectedItemForRest,
} from "./helpers/helperFunctions";
import { IChangeKavData, IUpdateCarDriver } from "../../../../modules/API/Distribution/models/fetchData";
import SelectFromTheList from "../../../../generic/customSearchComponents/SelectFromTheList";
import { ITableOfTableItem } from "../../../../modules/API/Defaults/models/tableOfTables";
import MultiSelect from "./MultiSelect";
import { ITransportAreaItem } from "../../../../../models/Defaults/transportArea";
import NewKavSelector from "./Items/NewKavSelector";
import { format } from "date-fns";
import LogButton from "./Items/LogButton";
import ChangeDriverAndSave from "./ChangeDriverAndSave";
import RemarksControl from "./generic/RemarksControl";
import { Orders } from "../../../../modules/API/Orders/orders";
import { ContextModal } from "../../../../modules/modal/modalContext";
import { currentPalletWeightValue, machineСapacity } from "./helpers/summuryKavInfo";
import CircleActiveFilterBtn from "../../../../generic/particles/CircleActiveFilterBtn";
import AcceptIcon from "../../../../generic/icons/IconLib/CheckIcon";
import ChecklistIcon from "../../../../generic/icons/IconLib/ChecklistIcon";
import CloseIcon from "../../../../generic/icons/IconLib/XIcon";
import ErrorIcon from "../../../../generic/icons/IconLib/AlertCircleIcon";
import InvoiceIcon from "../../../../generic/icons/IconLib/InvoiceIcon";
import SortIcon from "../../../../generic/icons/IconLib/SortIcon";
import PackageOrderTrucks from "./Modals/PackageOrderTrucks";
import useDataFetcher from "../knisot/hooks/useFetchData";
import { IClientItem } from "../../../../modules/API/Defaults/models/clients";
import { Packages } from "../../../../modules/API/Packages/packages";
import { IOrderPackageTitle, IPackageOrderData } from "../../../../modules/API/Packages/models/createPackageOrderModel";
import { IPackPalletDefaultClient } from "../../../../modules/API/Packages/models/arizotLine";
import selectBrodMigdal from "../../../../generic/utils/selectBrodMigdal";
import StatisticModalLayout from "../../../../generic/Layouts/StatisticModalLayout";
import StatisticTable from "./StatisticTable/StatisticTable";
import DropdownByIcon from "../azmanotRehesh/Layouts/DropdownByIcon";
import KavFilter from "./Components/KavFilter";
import SortSelector from "./Components/SortSelector";
import DropdownTooltip from "../../../../generic/tooltips/DropdownTooltip";
import { useDndAutoScroll } from "../../../../generic/hooks/useDndAutoScroll";

type Props = {};

interface IHashData {
  index: number;
  clients: Record<number, IHashDataClient>;
}
interface IHashDataClient {
  index: number;
  orders: Record<number, IHashDataOrder>;
}
interface IHashDataOrder {
  index: number;
  pallets: Record<number, IHashDataPallet>;
}
interface IHashDataPallet {
  index: number;
}
interface ILog {
  SourceID: number,
  Order: number[],
  Client: string,
  DestinationID: number,
  Pallets: number[],
  Date: string,

}
interface IRemarkHolder {
  item: IOrderItem;
  kavID: number;
  customerID: number;
}
const reorder = (
  list: ICustomerItem[],
  startIndex: number,
  endIndex: number
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const genDragId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `dragid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

const decorateTreeWithDragIds = (tree: IKavDataItem[]): IKavDataItem[] =>
  tree.map((kav) => ({
    ...kav,
    Customers: kav.Customers.map((c) =>
      c.__dragId ? c : { ...c, __dragId: genDragId() }
    ),
  }));

const buildHashForTree = (
  tree: IKavDataItem[]
): Record<number, IHashData> => {
  return tree.reduce(
    (acc: Record<number, IHashData>, cur, index) => {
      const clients = cur?.Customers.reduce(
        (clientsHash: Record<number, IHashDataClient>, client, indexClient) => {
          const orders = client?.Orders?.reduce(
            (ordersHash: Record<number, IHashDataOrder>, order, orderIndex) => {
              const pallets = order?.PalletsList?.reduce(
                (palletsHash: Record<number, IHashDataPallet>, pallet, palletIndex) => {
                  palletsHash[pallet.Pallet_Num] = { index: palletIndex };
                  return palletsHash;
                },
                {}
              );
              ordersHash[order.OrderID] = {
                index: orderIndex,
                pallets: pallets || {},
              };
              return ordersHash;
            },
            {}
          );
          clientsHash[client.CustomerID] = {
            index: indexClient,
            orders: orders || {},
          };
          return clientsHash;
        },
        {}
      );
      acc[cur.AreaID] = { index, clients: clients || {} };
      return acc;
    },
    {}
  );
};

// Custom collision detection tailored to two distinct drag kinds:
//   1. Kav reorder (source container = 'general'): the draggable is an
//      outer SortableKavWrapper, so only `kav-*` droppables are valid
//      targets.
//   2. Customer drag (source container = AreaID string): the draggable
//      is a customer card. Inner per-kav droppables (id = AreaID string)
//      and customer sortables are valid; outer `kav-*` wrappers must be
//      excluded, otherwise closestCorners tends to pick the whole kav
//      column for empty new kavs and land on containerId 'general',
//      which the drag handlers correctly refuse.
// Primary strategy is pointerWithin (follows the real pointer, works
// inside scrolled containers where rect-based strategies go stale),
// with closestCorners as a fallback for brief moments when the pointer
// sits between droppables.
const truckCollisionDetection: CollisionDetection = (args) => {
  const activeContainer = args.active.data.current?.sortable?.containerId;
  const isKavReorder = activeContainer === 'general';

  const filteredArgs = {
    ...args,
    droppableContainers: args.droppableContainers.filter((d) => {
      const isKavWrapper = typeof d.id === 'string' && d.id.startsWith('kav-');
      return isKavReorder ? isKavWrapper : !isKavWrapper;
    }),
  };

  const pointerHits = pointerWithin(filteredArgs);
  if (pointerHits.length > 0) return pointerHits;
  return closestCorners(filteredArgs);
};

const SortableKavWrapper: React.FC<{ areaID: number; children: React.ReactNode }> = ({ areaID, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `kav-${areaID}` });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <WrapperKav ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </WrapperKav>
  );
};

const TrucksMain = (props: Props) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.mainReducer.user);
  const isKablan = user?.rights?.includes("TRCOMPANY");

  const { array, dict } = useSelector(
    (state: RootState) => state.defaults.transportAreas
  );
  const drivers: IWorkerItem[] = useSelector(
    (state: RootState) => state.defaults.workerList
  ).filter((item: IWorkerItem) =>
    item.Role_108 === 4 && (
      isKablan ? item.FreightCompanyID === +(user?.code_internal || 0) : true
    )
  );

  const sessionLogData: ILog[] = useMemo(() => {
    const data = sessionStorage.getItem('log')
    if (data) return JSON.parse(data)
    return []
  }, [])
  const [modal, setModal] = useContext(ContextModal);
  const [logData, setLogData] = useState<ILog[]>(sessionLogData)
  const [deliveryCodeList, setDeliveryCodeList] = useState([]);
  const [deliveryID, setDeliveryID] = useState<number | null>(null);

  const defaults = useSelector((state: RootState) => state.defaults?.defaults);
  const ignorDirection = selectBrodMigdal(defaults, 593, 'Num_Value')
  const isLikutSortByKav = selectBrodMigdal(defaults, 649, 'Logical_Value')
  const [marketingAreaList, setMarketingAreaList] = useState<
    ITableOfTableItem[]
  >([]);
  const [selectedAreas, setSelectedAreas] = useState<ITableOfTableItem[]>([]);
  const [isOpenChangeDriver, setIsOpenChangeDriver] = useState(false);
  const [isOpenLog, setIsOpenLog] = useState(false);
  const [isActiveCheckFilter, setIsActiveCheckFilter] = useState(false);
  // SWITCH VIEW KAV
  const [witchViewKav, setWitchViewKav] = useState<number>(0);

  const [isOpenDashboard, setIsOpenDashboard] = useState(false);
  const [isOpenTable, setIsOpenTable] = useState(false);
  const [isOpenRemark, setIsOpenRemark] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<IRemarkHolder>();
  const [orderTree, setOrderTree] = useState<IKavDataItem[]>([]);
  const [selectedKav, setSelectedKav] = useState<IKavDataItem>();
  const [isOpenPackageOrder, setIsOpenPackageOrder] = useState(false);
  const [isNeedToSortLikut, setIsNeedToSortLikut] = useState('0');
  const [isFilterByProblems, setIsFilterByProblems] = useState(false);

  const [carsInfo, setCarsInfo] =
    useState<{ list: ICarItem[]; dict: TCarDict }>();
  const [date, setDate] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>("");
  const [activeClient, setActiveClient] = useState<{ customer: ICustomerItem; area: number } | null>(null);
  const [mergeHover, setMergeHover] = useState<{ areaID: number; customerID: number } | null>(null);
  const dragSnapshotRef = useRef<IKavDataItem[] | null>(null);
  const dragSourceAreaRef = useRef<number | null>(null);
  const lastCrossKavMoveRef = useRef<number>(0);
  const kavRibbonRef = useRef<HTMLDivElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  useDndAutoScroll(kavRibbonRef, {
    enabled: isDragActive,
    threshold: 150,
    maxSpeed: 18,
    minIntervalMs: 20,
  });

  const getOrdersTree = async (date: string) => {
    dispatch({ type: LOAD });
    try {
      let res = await Trucks.getOrdersTree(date);
      setOrderTree(decorateTreeWithDragIds(res));
    } catch (err) {
    } finally {
      dispatch({ type: LOAD_END });
    }
  };
  const handleChangeSort = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if(!isLikutSortByKav) return
    const copy = [...orderTree]
    if(e.target.value === '1') {
      copy.sort((a, b) => a.PickupOrder - b.PickupOrder)
    } else {
      copy.sort((a, b) => a.Sort - b.Sort)
    }
    setOrderTree(copy)
    setIsNeedToSortLikut(e.target.value)

  }
  const handleOpenPackageOrder = (item?: IKavDataItem) => {
    if (item) {
      setSelectedKav(item);
    }
    setIsOpenPackageOrder(!isOpenPackageOrder);
  }

  const {
    data: clientList
  } = useDataFetcher<{ clients: IClientItem[], message: string }>(
    Defaults.getClientList, 'data'
  )

  const {
    data: packsData
  } = useDataFetcher<{ defaultClient: IPackPalletDefaultClient[] }>(
    Packages.getPackPalletList, 'data'
  )

  const getDeliveryCodes = async () => {
    try {
      let res = await Defaults.getDeliveryCodeList();
      setDeliveryCodeList(res.deliveryCodes);
    } catch (err) { }
  };

  const getCars = async () => {
    try {
      let res: { cars: ICarItem[] } = await Defaults.getCarList();
      let newSet: TCarDict = {};

      res.cars.forEach((item) => {
        newSet[item.CarID] = item;
      });
      setCarsInfo({
        list: isKablan ? res.cars.filter(item => item.FreightCompanyID === +(user?.code_internal || 0) && item.Active) : res.cars.filter(item => item.Active),
        dict: newSet,
      });
    } catch (err) { }
  };

  const getMarketingAreaList = async () => {
    try {
      let res = await Defaults.getDataFromTableOfTables(300);
      setMarketingAreaList(res);
    } catch (err) { }
  };

  const patchDriver = async (id: number) => {
    if (!selectedKav) return
    try {
      await Trucks.patchDriver(selectedKav?.AreaID, { CarId: id })
      let copy = [...orderTree]
      copy[hashData[selectedKav.AreaID].index] = {
        ...copy[hashData[selectedKav.AreaID].index],
        CarId: id,

      }
      setOrderTree(copy)
      dict[selectedKav.AreaID].CarId = id
      setIsOpenChangeDriver(false)
    } catch (err) {

    }
  };
  const changeDriver = (item: IKavDataItem) => {
    setSelectedKav(item);
    setIsOpenChangeDriver(true);
  }
  const setSort = async (arr: IKavDataItem[] ) => {
    try {
      await Trucks.setSort(arr.map(e => e.AreaID))
    } catch (err) {

    } 
  }

  const setLikutSort = async (arr: IKavDataItem[]) => {
    try {
      await Trucks.setLikutSort(date, arr.map(e => e.AreaID))
    } catch (err) {

    }
  }
  /**
 * Handles the drag end event when a draggable item is dropped.
 * @param result - The result object containing information about the drag event.
 */
  const onDragStart = (event: DragStartEvent) => {
    setIsDragActive(true);
    const sourceContainer = event.active.data.current?.sortable?.containerId;
    if (sourceContainer == null || sourceContainer === 'general') {
      dragSourceAreaRef.current = null;
      dragSnapshotRef.current = null;
      return;
    }
    const areaID = parseInt(String(sourceContainer));
    const customerID: number | undefined = event.active.data.current?.customerID;
    if (Number.isNaN(areaID) || customerID == null) return;
    dragSourceAreaRef.current = areaID;
    dragSnapshotRef.current = orderTree;
    const kav = orderTree.find((k) => k.AreaID === areaID);
    const customer = kav?.Customers.find((c) => c.CustomerID === customerID);
    if (customer) setActiveClient({ customer, area: areaID });
  };

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeContainer = active.data.current?.sortable?.containerId;
    const overContainer = over.data.current?.sortable?.containerId ?? over.id;
    if (activeContainer == null || overContainer == null) return;
    if (activeContainer === 'general' || overContainer === 'general') return;
    if (activeContainer === overContainer) {
      if (mergeHover) setMergeHover(null);
      return;
    }
    // Throttle cross-kav moves so the cursor hovering right on a kav boundary
    // does not ping-pong the card back and forth between columns.
    if (Date.now() - lastCrossKavMoveRef.current < 140) return;
    const sourceAreaID = parseInt(String(activeContainer));
    const destAreaID = parseInt(String(overContainer));
    const customerID: number | undefined = active.data.current?.customerID;
    if (
      Number.isNaN(sourceAreaID) ||
      Number.isNaN(destAreaID) ||
      customerID == null
    ) return;

    const destKav = orderTree.find((k) => k.AreaID === destAreaID);
    if (!destKav) return;
    const alreadyInDest = destKav.Customers.some((c) => c.CustomerID === customerID);

    if (alreadyInDest) {
      if (
        !mergeHover ||
        mergeHover.areaID !== destAreaID ||
        mergeHover.customerID !== customerID
      ) {
        setMergeHover({ areaID: destAreaID, customerID });
      }
      if (dragSnapshotRef.current && orderTree !== dragSnapshotRef.current) {
        setOrderTree(dragSnapshotRef.current);
      }
      return;
    }

    if (mergeHover) setMergeHover(null);

    const overIndex = over.data.current?.sortable?.index;
    lastCrossKavMoveRef.current = Date.now();
    startTransition(() => {
      setOrderTree((prev) => {
        const sourceKavIdx = prev.findIndex((k) => k.AreaID === sourceAreaID);
        const destKavIdx = prev.findIndex((k) => k.AreaID === destAreaID);
        if (sourceKavIdx < 0 || destKavIdx < 0) return prev;
        const sourceKav = prev[sourceKavIdx];
        const customer = sourceKav.Customers.find((c) => c.CustomerID === customerID);
        if (!customer) return prev;
        const destKavCur = prev[destKavIdx];
        if (destKavCur.Customers.some((c) => c.CustomerID === customerID)) return prev;
        const insertIdx =
          typeof overIndex === 'number' ? overIndex : destKavCur.Customers.length;
        const newSourceCustomers = sourceKav.Customers.filter(
          (c) => c.CustomerID !== customerID
        );
        const newDestCustomers = [
          ...destKavCur.Customers.slice(0, insertIdx),
          customer,
          ...destKavCur.Customers.slice(insertIdx),
        ];
        const copy = [...prev];
        copy[sourceKavIdx] = { ...sourceKav, Customers: newSourceCustomers };
        copy[destKavIdx] = { ...destKavCur, Customers: newDestCustomers };
        return copy;
      });
    });
  };

  const onDragCancel = () => {
    setIsDragActive(false);
    setActiveClient(null);
    setMergeHover(null);
    if (dragSnapshotRef.current) setOrderTree(dragSnapshotRef.current);
    dragSnapshotRef.current = null;
    dragSourceAreaRef.current = null;
  };

  const onDragEnd = (event: DragEndEvent) => {
    setIsDragActive(false);
    setActiveClient(null);
    setMergeHover(null);

    const { active, over } = event;
    const snap = dragSnapshotRef.current;
    const originalSourceArea = dragSourceAreaRef.current;
    dragSnapshotRef.current = null;
    dragSourceAreaRef.current = null;

    const rollback = () => { if (snap) setOrderTree(snap); };

    if (!over) { rollback(); return; }

    const sourceContainer = active.data.current?.sortable?.containerId;
    const destinationContainerRaw = over.data.current?.sortable?.containerId ?? over.id;
    if (sourceContainer == null || destinationContainerRaw == null) {
      rollback();
      return;
    }

    // Kav reorder branch — onDragOver does not touch state here, so orderTree
    // equals snapshot for this drag type. Use existing logic untouched.
    if (sourceContainer === 'general') {
      const activeSortableIndex = active.data.current?.sortable?.index ?? 0;
      const overIdxRaw = over.data.current?.sortable?.index;
      const destIdx = typeof overIdxRaw === 'number' ? overIdxRaw : 0;
      if (destIdx === activeSortableIndex) return;
      if (isKablan || !isLikutSortByKav) return;
      const renderList = render.filter(item => item.AreaID !== 0);
      const copyDataArray = [...orderTree];
      const findSource = copyDataArray.findIndex(e => e.AreaID === renderList[activeSortableIndex]?.AreaID);
      const findDestination = copyDataArray.findIndex(e => e.AreaID === renderList[destIdx]?.AreaID);
      if (findSource < 0 || findDestination < 0) return;
      const [removed] = copyDataArray.splice(findSource, 1);
      copyDataArray.splice(findDestination, 0, removed);
      if (isNeedToSortLikut === "1") {
        setOrderTree(copyDataArray.map((e, i) => ({ ...e, PickupOrder: i })));
        setLikutSort(copyDataArray);
      } else {
        setOrderTree(copyDataArray.map((e, i) => ({ ...e, Sort: i })));
        setSort(copyDataArray);
      }
      return;
    }

    // Customer drag — base logic on snapshot so ephemeral onDragOver moves
    // don't poison sourceIndex / hashData lookups.
    const baseTree = snap ?? orderTree;
    const baseHash = snap ? buildHashForTree(snap) : hashData;
    const copyDataArray = [...baseTree];

    const sourceID = originalSourceArea ?? parseInt(String(sourceContainer));
    const destinationID = parseInt(String(destinationContainerRaw));
    const draggableItemId: number | undefined = active.data.current?.customerID;
    if (
      Number.isNaN(sourceID) ||
      Number.isNaN(destinationID) ||
      draggableItemId == null
    ) {
      rollback();
      return;
    }
    if (!baseHash[sourceID] || !baseHash[sourceID].clients[draggableItemId]) {
      rollback();
      return;
    }
    if (!baseHash[destinationID]) {
      rollback();
      return;
    }

    const sourceIndex = baseHash[sourceID].clients[draggableItemId].index;

    // destIndex resolution:
    // - self-over (cursor on the active card's own ephemeral ghost) → use the
    //   ghost's current position in the live orderTree.
    // - over some other customer in dest → use that customer's snapshot index
    //   (neighbours around it aren't shifted in snapshot).
    // - over the kav container itself / empty space → end of snapshot dest list.
    const overCustomerID: number | undefined = over.data.current?.customerID;
    let destIndex: number;
    if (overCustomerID === draggableItemId) {
      const destCurrent = orderTree.find((k) => k.AreaID === destinationID);
      const ghostIdx = destCurrent?.Customers.findIndex(
        (c) => c.CustomerID === draggableItemId
      ) ?? -1;
      if (ghostIdx >= 0) {
        destIndex = ghostIdx;
      } else {
        destIndex = baseTree[baseHash[destinationID].index].Customers.length;
      }
    } else if (
      overCustomerID != null &&
      baseHash[destinationID].clients[overCustomerID] != null
    ) {
      destIndex = baseHash[destinationID].clients[overCustomerID].index;
    } else {
      destIndex = baseTree[baseHash[destinationID].index].Customers.length;
    }

    if (sourceID === destinationID && sourceIndex === destIndex) {
      rollback();
      return;
    }

    let apiData: IChangeKavData = {
      Orders: [],
      TransferZone: destinationID,
    };
    let logElement: ILog;

    if (sourceID === destinationID) {
      const reordered = reorder(
        copyDataArray[baseHash[sourceID].index].Customers,
        sourceIndex,
        destIndex
      );
      copyDataArray[baseHash[sourceID].index] = {
        ...copyDataArray[baseHash[sourceID].index],
        Customers: reordered,
      };
      const sortDataOrders = copyDataArray[baseHash[sourceID].index].Customers
        .map(item => item.Orders.map(order => order.OrderID))
        .flat();
      sortKav(sortDataOrders, copyDataArray[baseHash[sourceID].index].AreaID);
      setOrderTree(copyDataArray);
      return;
    }

    let sourseClientsList = [...copyDataArray[baseHash[sourceID].index].Customers];
    let destenationClientsList = [...copyDataArray[baseHash[destinationID].index].Customers];

    const draggedClient = Object.assign(
      {},
      sourseClientsList[baseHash[sourceID].clients[draggableItemId].index]
    );
    const isSomethingSelected = draggedClient.Orders.some((e) => e.Selected);
    const isExistsInDestList = baseHash[destinationID].clients[draggableItemId] !== undefined;
    const formatedDest = helperFormatSourceSelectedItemForDrag(
      sourseClientsList[baseHash[sourceID].clients[draggableItemId].index].Orders
    );
    const formatedSource = helperFormatSourceSelectedItemForRest(
      sourseClientsList[baseHash[sourceID].clients[draggableItemId].index].Orders
    );

    if (isExistsInDestList) {
      let newDestClientOrders;
      if (isSomethingSelected) {
        apiData = helperApiDataCollector(apiData, formatedDest);
        newDestClientOrders = halperMergeOrders(
          destenationClientsList[baseHash[destinationID].clients[draggableItemId].index].Orders,
          formatedDest
        );
        sourseClientsList.splice(sourceIndex, 1);
        if (formatedSource.length > 0) {
          sourseClientsList.splice(sourceIndex, 0, {
            ...draggedClient,
            __dragId: genDragId(),
            Orders: formatedSource,
          });
        }
        logElement = {
          SourceID: sourceID,
          Order: formatedDest.map(e => e.OrderID),
          Client: draggedClient?.Customer_Name?.trim(),
          DestinationID: destinationID,
          Pallets: formatedDest.map(e => e.PalletsList.map(pallet => pallet.Pallet_Num)).flat(),
          Date: format(new Date(), 'HH:mm'),
        };
      } else {
        apiData = helperApiDataCollector(apiData, draggedClient.Orders);
        newDestClientOrders = halperMergeOrders(
          destenationClientsList[baseHash[destinationID].clients[draggableItemId].index].Orders,
          draggedClient.Orders
        );
        sourseClientsList.splice(sourceIndex, 1);
        logElement = {
          SourceID: sourceID,
          Order: draggedClient.Orders.map(e => e.OrderID),
          Client: draggedClient?.Customer_Name?.trim(),
          DestinationID: destinationID,
          Pallets: draggedClient.Orders.map(e => e.PalletsList.map(pallet => pallet.Pallet_Num)).flat(),
          Date: format(new Date(), 'HH:mm'),
        };
      }
      draggedClient.Orders = newDestClientOrders;
      destenationClientsList[baseHash[destinationID].clients[draggableItemId].index] = draggedClient;
    } else {
      if (isSomethingSelected) {
        apiData = helperApiDataCollector(apiData, formatedDest);
        sourseClientsList.splice(sourceIndex, 1);
        if (formatedSource.length > 0) {
          sourseClientsList.splice(sourceIndex, 0, {
            ...draggedClient,
            __dragId: genDragId(),
            Orders: formatedSource,
          });
        }
        draggedClient.Orders = formatedDest;
        destenationClientsList.splice(destIndex, 0, draggedClient);
        logElement = {
          SourceID: sourceID,
          Order: formatedDest.map(e => e.OrderID),
          Client: draggedClient?.Customer_Name?.trim(),
          DestinationID: destinationID,
          Pallets: formatedDest.map(e => e.PalletsList.map(pallet => pallet.Pallet_Num)).flat(),
          Date: format(new Date(), 'HH:mm'),
        };
      } else {
        sourseClientsList.splice(sourceIndex, 1);
        destenationClientsList.splice(destIndex, 0, draggedClient);
        apiData = helperApiDataCollector(apiData, draggedClient.Orders);
        logElement = {
          SourceID: sourceID,
          Order: draggedClient.Orders.map(e => e.OrderID),
          Client: draggedClient?.Customer_Name?.trim(),
          DestinationID: destinationID,
          Pallets: draggedClient.Orders.map(e => e.PalletsList.map(pallet => pallet.Pallet_Num)).flat(),
          Date: format(new Date(), 'HH:mm'),
        };
      }
    }

    if (draggedClient.PO_Num !== 0) {
      apiData.Orders.push({
        OrderID: draggedClient.PO_Num,
        Pallets: [],
        Type: "PackageOrder",
      });
    }

    copyDataArray[baseHash[sourceID].index] = {
      ...copyDataArray[baseHash[sourceID].index],
      Customers: sourseClientsList,
    };
    copyDataArray[baseHash[destinationID].index] = {
      ...copyDataArray[baseHash[destinationID].index],
      Customers: destenationClientsList,
    };

    const sortDataOrders = copyDataArray[baseHash[destinationID].index].Customers
      .map(item => item.Orders.map(order => order.OrderID))
      .flat();
    sortKav(sortDataOrders, copyDataArray[baseHash[destinationID].index].AreaID);
    setOrderTree(copyDataArray);
    patchPallet(apiData);
    setLogData((prev) => [...logData, logElement]);
  };

  /* The above code is creating a hash data structure using the `useMemo` hook in a TypeScript React
  component. */
  const hashData = useMemo(() => buildHashForTree(orderTree), [orderTree]);

  const dataForAddKav: ITransportAreaItem[] = useMemo(() => {
    return [...array]?.filter(e => !hashData[e.AreaID]).sort((a, b) => a.Sort - b.Sort);
  }, [array, hashData]);

  const patchPallet = async (body: IChangeKavData) => {
    try {
      let res = await Trucks.changeKav(body);
      // console.log(res.data)
    } catch (err) {
      console.log(err);
    }
  };
  /**
   * The function `handleSelectToDrag` is used to handle the selection of elements in a tree structure
   * and update the state accordingly.
   * @param {number} elementID - The ID of the element to be selected/deselected for dragging.
   * @param {number} kavID - The `kavID` parameter represents the ID of a specific kav (customer) in the
   * order tree.
   * @param {number} CustomerID - CustomerID is the ID of the customer within the order tree.
   * @param {number} OrderID - The `OrderID` parameter represents the ID of the order.
   * @param {boolean} [isOrder] - The `isOrder` parameter is a boolean flag that indicates whether the
   * selected element is an order or not. If `isOrder` is `true`, it means the selected element is an
   * order. If `isOrder` is `false` or not provided, it means the selected element is a
   */
  const handleSelectToDrag = (
    elementID: number,
    kavID: number,
    CustomerID: number,
    OrderID: number,
    isOrder?: boolean
  ) => {
    let newArrData = [...orderTree];

    if (!isOrder) {
      const selectedElement =
        newArrData[hashData[kavID].index].Customers[
          hashData[kavID].clients[CustomerID].index
        ].Orders[hashData[kavID].clients[CustomerID].orders[OrderID].index]
          .PalletsList[
        hashData[kavID].clients[CustomerID].orders[OrderID].pallets[elementID]
          .index
        ];
      newArrData[hashData[kavID].index].Customers[
        hashData[kavID].clients[CustomerID].index
      ].Orders[
        hashData[kavID].clients[CustomerID].orders[OrderID].index
      ].PalletsList[
        hashData[kavID].clients[CustomerID].orders[OrderID].pallets[
          elementID
        ].index
      ].Selected = !selectedElement.Selected;

      let tempForCalc =
        newArrData[hashData[kavID].index].Customers[
          hashData[kavID].clients[CustomerID].index
        ].Orders[hashData[kavID].clients[CustomerID].orders[OrderID].index]
          .PalletsList;
      let selectedPallet = tempForCalc.some((e) => e.Selected);
      if (selectedPallet) {
        newArrData[hashData[kavID].index].Customers[
          hashData[kavID].clients[CustomerID].index
        ].Orders[
          hashData[kavID].clients[CustomerID].orders[OrderID].index
        ].Selected = true;
      } else {
        newArrData[hashData[kavID].index].Customers[
          hashData[kavID].clients[CustomerID].index
        ].Orders[
          hashData[kavID].clients[CustomerID].orders[OrderID].index
        ].Selected = false;
      }
    } else {
      const selectedElement =
        newArrData[hashData[kavID].index].Customers[
          hashData[kavID].clients[CustomerID].index
        ].Orders[hashData[kavID].clients[CustomerID].orders[OrderID].index];
      newArrData[hashData[kavID].index].Customers[
        hashData[kavID].clients[CustomerID].index
      ].Orders[
        hashData[kavID].clients[CustomerID].orders[OrderID].index
      ].Selected = !selectedElement.Selected;
      if (selectedElement.PalletsList.length > 0) {
        newArrData[hashData[kavID].index].Customers[
          hashData[kavID].clients[CustomerID].index
        ].Orders[
          hashData[kavID].clients[CustomerID].orders[OrderID].index
        ].PalletsList.map((item) => {
          item.Selected = selectedElement.Selected;
          return item;
        });
      }
    }
    setOrderTree(newArrData);
  };

  const selectDelivery = (value: string) => {
    if (value) {
      setDeliveryID(+value);
    } else {
      setDeliveryID(null);
    }
  };
  const collectSelectedDistricts = (
    item: ITableOfTableItem | null,
    clear?: boolean
  ) => {
    if (clear || !item) {
      setSelectedAreas([]);
      dispatch({ type: SELECT_MARKETING_AREA, payload: [] });
      return;
    }
    let newArr = [...selectedAreas];
    let flag = selectedAreas.find((e) => e.SecondCode === item.SecondCode);
    if (!flag) {
      newArr = [...newArr, item];
    } else {
      let elemIndex = newArr.indexOf(item);
      newArr.splice(elemIndex, 1);
    }
    setSelectedAreas(newArr);
    dispatch({ type: SELECT_MARKETING_AREA, payload: newArr });
  };
  const handleSelectNewKav = (target: string) => {
    let targetInt = +target;
    const found = hashData[targetInt]
    if (found) return;
    let newTreeArray = [...orderTree];
    let newKav: IKavDataItem = {
      AreaDesc: dict[targetInt].AreaDesc?.trim(),
      AreaID: dict[targetInt].AreaID,
      CarId: dict[targetInt].CarId,
      DriverName: dict[targetInt].DriverName,
      Sort: dict[targetInt].Sort,
      LastDistDate: '',
      Customers: [],
      isNew: true,
      DriverWorkerId: 0,
      ShowToDriver: false,
      Actions: [],
      PickupOrder: 0
    }
    newTreeArray.splice(1, 0, newKav)
    setOrderTree([...newTreeArray])

  };
  // console.log(orderTree.filter(tree => tree.Customers.filter(c => c.Orders.filter(o => o.Delivery_Code === 1).length > 0).length > 0))
  const render = useMemo(() => {
    // if (!searchInput && !deliveryID && selectedAreas.length === 0 ) return orderTree;
    const filter = orderTree.map((item) => {
      // if (item.AreaID === 0) return true;
      return {
        ...item,
        Customers:
          item.Customers?.filter(
            (customer) =>
              // (searchInput ? customer.Customer_Name?.trim()
              // .toLowerCase()
              // .includes(searchInput.toLowerCase()) : true)
              //    &&
              ((deliveryID ?
                customer.Orders.filter((order) => {
                  if (order.Delivery_Code === deliveryID) {

                  }
                  return order.Delivery_Code === deliveryID
                }).length > 0
                : true) &&
                (
                  selectedAreas.length > 0 ?
                    selectedAreas.some((e) => e.SecondCode === customer.Marketing_AreaID)
                    : true
                ))
              ||
              customer.Type === 1

          )
      }
    }).filter(e => {
      return (e.Customers.length > 0 || e.isNew) &&
        (witchViewKav === 1 ? e.ShowToDriver : witchViewKav === 2 ? !e.ShowToDriver : true) &&
        (isFilterByProblems ? e.Customers.some(customer => customer.Orders.some(order => order.IsProblem)) : true)
    }
    );

    if (filter[0]?.AreaID !== 0) {
      filter.unshift({ ...orderTree[0], Customers: [] })
    }
    const final = filter.filter(kav => {
      if (kav.AreaID === 0) return true
      return kav.Customers.filter(customer =>
      (searchInput ? customer.Customer_Name?.trim()
        .toLowerCase()
        .includes(searchInput.toLowerCase()) : true)
      ).length > 0
        ||
        kav.AreaDesc?.trim().toLowerCase().includes(searchInput.toLowerCase())
        ||
        (carsInfo?.dict[kav.CarId] ? carsInfo?.dict[kav.CarId].DriverName.toLowerCase()?.trim().includes(searchInput.toLowerCase()) : false)
        ||
        kav.DriverName?.trim().toLowerCase().includes(searchInput.toLowerCase())
    }
    )
    return final.reverse();
  }, [isFilterByProblems, witchViewKav, orderTree, searchInput, deliveryID, selectedAreas]);
  useEffect(() => {
    if (!date) return;
    getOrdersTree(date);
  }, [date]);

  useEffect(() => {
    getCars();
    getDeliveryCodes();
    getMarketingAreaList();
  }, []);

  const sortKav = async (arr: number[], kavID: number) => {
    try {
      await Trucks.sortKavByOrders(arr, kavID)
    } catch (Err) {

    }
  };
  const newPatchDriverCar = async (carID: number, DriverID: number, driverName: string, isPermanent?: boolean) => {
    if (!selectedKav) return

    try {
      let copy = [...orderTree]
      const putData = {
        "CarId": carID,
        "ChangePermanent": !!isPermanent,
        "Date": date || '',
        "DriverWorkerId": DriverID,
        "ShowToDriver": selectedKav.ShowToDriver
      }
      let res = await Trucks.updateCarDriver(selectedKav.AreaID, putData)
      copy[hashData[selectedKav.AreaID].index] = {
        ...copy[hashData[selectedKav.AreaID].index],
        CarId: carID,
        DriverName: driverName?.trim(),
        DriverWorkerId: DriverID

      }
      setOrderTree(copy)
      dict[selectedKav.AreaID].CarId = carID
      setIsOpenChangeDriver(false)
    } catch (err) {
      setModal({
        open: true,
        value: "משהו השתבש",
        mood: false
      })
      setTimeout(() => {
        setModal({
          ...modal,
          open: false
        })
      }, 3000)
    }
  }

  const driverVisible = async (kavID: number, isShow: boolean) => {
    let data = {
      Date: date || '',
      ShowToDriver: isShow
    }
    try {
      await Trucks.showDriverKav(kavID, data)
      let copy = [...orderTree]
      copy[hashData[kavID].index] = {
        ...copy[hashData[kavID].index],
        ShowToDriver: isShow
      }
      setOrderTree(copy)
    } catch (err) {

    }
  }
  const selectDriverP = {
    kav: selectedKav,
    saveFunc: patchDriver,
    list: carsInfo?.list,
  };
  const selectDriverAndCarP = {
    kav: selectedKav,
    saveFunc: newPatchDriverCar,
    listCars: carsInfo?.list,
    listCarsDrivers: drivers,
    driverName: selectedKav?.CarId ? carsInfo?.dict[selectedKav?.CarId]?.DriverName : '',
  };

  const handleCreatePackageOrder = async (
    clientInfo: {
      clientID: number,
      selectID: number
    }
  ) => {
    const defClientTranzitID = packsData?.defaultClient[0].ClientID || 0

    const data: IPackageOrderData = {
      ClientID_Recipient: clientInfo.selectID === 1 ? defClientTranzitID : clientInfo.clientID,
      ClientID_Sender: clientInfo.selectID === 1 ? clientInfo.clientID : defClientTranzitID,
      Reference: '',
      PD_Num: 0,
      PO_Date: date,
      Qty: 0,
      Vehicle_Code: selectedKav?.CarId || 0,
      Vehicle_Num: 0,
      Driver_Name: selectedKav?.DriverName || '',
      PO_Status: 0,
      Remarks: '',
      PO_Time: '0',
      Packs_Pallets: [],
      TransferZone: selectedKav?.AreaID || 0,
      Pickup: true,
    }
    try {
      let res: IOrderPackageTitle = await Packages.createPackageOrder(data)
      setIsOpenPackageOrder(false)
      let copy = [...orderTree]
      copy[hashData[selectedKav?.AreaID || 0].index].Customers.push({
        ClientID: defClientTranzitID === res.ClientID_Sender ?
          res.ClientID_Recipient : res.ClientID_Sender,
        CustomerID: defClientTranzitID === res.ClientID_Sender ?
          res.ClientID_Recipient : res.ClientID_Sender,
        Customer_Name: defClientTranzitID === res.ClientID_Sender ?
          res.RecipientName.trim() : res.SenderName.trim(),
        DistributNote: '',
        LastAction: {
          Action: 0,
          ActionDescription: "",
          ActionFlag: false,
          LogDate: "",
          LogTime: ""
        },
        Marketing_AreaID: 0,
        Orders: [],
        PD_Num: 0,
        PO_Num: res.PO_Num,
        Phone: '',
        ShippAddress: '',
        Type: 1,
        __dragId: genDragId(),
      })
      setOrderTree(copy)
      setModal({
        open: true,
        value: "הזמנה נוצרה בהצלחה",
        mood: true
      })
      setTimeout(() => {
        setModal({
          ...modal,
          open: false
        })
      }, 3000)
    } catch (err) {
      setModal({
        open: true,
        value: "משהו השתבש",
        mood: false
      })
      setTimeout(() => {
        setModal({
          ...modal,
          open: false
        })
      }, 3000)
      console.log(err)
    }
  }
  const handleSaveRemarks = async (likut: string, hafa: string) => {
    if (!hashData || !selectedOrder) return
    const patchData = {
      "collectComm": likut,
      "distributNote": hafa,
      "pallets": [],
      "orders": [selectedOrder?.item.OrderID],
    }
    try {
      await Orders.patchOrderDataRemarks(patchData)

      let copy = [...orderTree]
      let newOrder = { ...selectedOrder?.item }
      newOrder.Collect_Comm = likut
      newOrder.DistributNote = hafa
      copy[hashData[selectedOrder.kavID].index].Customers[hashData[selectedOrder?.kavID].clients[selectedOrder?.customerID].index].Orders[hashData[selectedOrder?.kavID].clients[selectedOrder?.customerID].orders[selectedOrder?.item.OrderID].index] = newOrder
      setOrderTree(copy)
      setIsOpenRemark(!isOpenRemark)

    } catch (err) {
      setModal({
        open: true,
        value: "משהו השתבש",
        mood: false
      })
      setTimeout(() => {
        setModal({
          ...modal,
          open: false
        })
      }, 3000)
    }
  }
  const remarkP = {
    item: selectedOrder?.item,
    saveFunc: handleSaveRemarks
  }
  const handleControlLogWindow = () => {
    setIsOpenLog(!isOpenLog)
  }

  const handleIsOpenRemark = () => {
    setIsOpenRemark(!isOpenRemark)
  }
  const handleFilterByProblems = () => {
    setIsFilterByProblems(!isFilterByProblems)
  }
  const handleClickSelectOrder = (item: IOrderItem, area: number, CustomerID: number) => {
    setSelectedOrder({
      item: item,
      kavID: area,
      customerID: CustomerID
    });
    setIsOpenRemark(!isOpenRemark)
  }
  const [isMassActive, setIsMassActive] = useState(false)

  /**
   * The function `bulkPostSaveKav` dispatches a load action, fetches data using the `massAction`
   * function, confirms the data using the `Trucks.bulkConfirm` method, updates the `orderTree` state
   * based on the fetched data, and dispatches a load end action.
   * @returns The function `bulkPostSaveKav` does not have a return statement, so it does not explicitly
   * return anything.
   */
  const bulkPostSaveKav = async () => {
    dispatch({ type: LOAD });
    const fetchData = massAction()
    if (fetchData.length === 0) return
    try {
      let res = await Trucks.bulkConfirm(fetchData)
      let orderTreeCopy = [...orderTree]
      fetchData.forEach((item) => {
        // console.log(item.ShowToDriver)
        orderTreeCopy[hashData[item.AreaID].index] = {
          ...orderTreeCopy[hashData[item.AreaID].index],
          ShowToDriver: item.ShowToDriver
        }
      })
      setOrderTree(orderTreeCopy)
    } catch (err) {
    } finally {
      dispatch({ type: LOAD_END });
    }
  }

  /**
   * The `massAction` function filters an array of `orderTree` objects based on certain conditions and
   * returns a modified version of the filtered objects.
   * @returns The function `massAction` returns an array of objects. Each object in the array has the
   * following properties: "AreaID", "CarId", "ChangePermanent", "Date", "DriverWorkerId", and
   * "ShowToDriver".
   */
  const massAction = () => {
    const activated = orderTree.filter((kav) => {
      if (kav.AreaID === 0) return false
      // if(kav.ShowToDriver === !isMassActive) return false
      if (kav.Customers.length === 0) return false
      const dataKav = currentPalletWeightValue(kav.Customers)
      const dataCar = machineСapacity(carsInfo?.dict, kav.CarId)

      const isOverWeigth = (dataKav.weightCalc > (dataCar.Max_Weight || 0) && dataKav.palletCalc > (dataCar.Pallets || 0))
      return !isOverWeigth
      // console.log(data)
    })
    setIsMassActive(!isMassActive)
    return activated.map((kav) => {
      let item = {
        "AreaID": kav.AreaID,
        "CarId": kav.CarId,
        "ChangePermanent": false,
        "Date": date || '',
        "DriverWorkerId": kav.DriverWorkerId,
        "ShowToDriver": !isMassActive
      }
      return item
    })
  }

  const packageOrderProps = {
    item: selectedKav,
    clientList: clientList?.clients || [],
    createPackageOrder: handleCreatePackageOrder,
    tranzitClient: packsData?.defaultClient[0] || {}
  }
  const statisticTableP = {
    carList: carsInfo?.list,
    carsDict: carsInfo?.dict,
    orderTree: orderTree,
    transportAreaDict: dict,
    ignorDirection: ignorDirection
  }
  const onDragMove = (event: DragMoveEvent) => {
    // retained for parity with the former onDragUpdate hook; currently a no-op
  }
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  // massAction()
  return (
    <MainWrapper >
      {/* {isOpenChangeDriver && (
        <ColumModal
          title="שינוי נהג"
          controlFunc={() => {
            setIsOpenChangeDriver(!isOpenChangeDriver);
          }}
          Children={SelectDriver}
          childrenProps={selectDriverP}
        />
      )} */}
      {
        isOpenPackageOrder &&
        <ColumModal
          title="הוספת הזמנה אריזה"
          controlFunc={() => handleOpenPackageOrder()}
          Children={PackageOrderTrucks}
          childrenProps={packageOrderProps}
        />
      }
      {
        isOpenRemark &&
        <ColumModal
          title={`הערות להזמנה ${selectedOrder?.item.OrderID}#`}
          controlFunc={handleIsOpenRemark}
          Children={RemarksControl}
          childrenProps={remarkP}
        />
      }
      {isOpenChangeDriver && (
        <ColumModal
          title="שינוי רכב\נהג לקו"
          controlFunc={() => {
            setIsOpenChangeDriver(!isOpenChangeDriver);
          }}
          Children={ChangeDriverAndSave}
          childrenProps={selectDriverAndCarP}
        />
      )}
      {
        isOpenLog &&
        <Logger
          controlFunc={setIsOpenLog}
          data={logData}
          transportAreaDict={dict}
        />
      }

      {/* {isOpenLog && (
        <Logger
          controlFunc={setIsOpenLog}
          data={logData}
          transportAreaDict={transportAreaDict}
        />
      )}
      {isOpenDashboard && (
        <DashboardTruck
          controllFunc={setIsOpenDeshboard}
          palletList={palletList}
          transportAreaDict={transportAreaDict}
          cars={carsInfo?.dict}
          ignorDirection={ignorDirection}
        />
      )}

      
        
      */
      }
      {isOpenTable && (
        <StatisticModalLayout
          controllFunc={setIsOpenTable}
          Children={StatisticTable}
          childrenProps={statisticTableP}
        />
      )}
      <HeadWrapper>
        <DropdownTooltip
          tooltipText="שמור כולם"
        >
        <CircleActiveFilterBtn
          withAction
          func={bulkPostSaveKav}
          children={isMassActive ? <span style={{ display: 'inline-flex', cursor: 'pointer' }} onClick={() => ''}><CloseIcon /></span> : <AcceptIcon />}
        />
        </DropdownTooltip>
        <DropdownTooltip
          tooltipText="לוג שינויים"
        >

          <LogButton
            handleControl={handleControlLogWindow}
          />
        </DropdownTooltip>
        <DropdownTooltip
          tooltipText="ריכוז לפי נהג"
        >

          <CircleActiveFilterBtn
            func={() => { setIsOpenTable(true) }}
            >
            <InvoiceIcon />
          </CircleActiveFilterBtn>
        </DropdownTooltip>
        <DatePickerModule
          multyDate={false}
          ignorBrod={false}
          saveWithoutFormat={false}
          disabled={false}
          setSelectedDateStart={setDate}
          setSelectedDateEnd={() => { }}
          isNeedWithoutFormated={false}
          defEndDate={false}
          selected={false}
          large={false}
        />
        <SelectFromTheList
          lable="סבב הפצה - הכל"
          list={deliveryCodeList}
          size="large"
          itemKey="Delivery_Code"
          fieldValue="Delivery_Code"
          dispalyFieldName="Delivery_Description"
          selectFunction={selectDelivery}
          selectedValue={deliveryID}
        />
          <DropdownTooltip
            tooltipText="אזורי שיווק"
          >

            <MultiSelect
              data={marketingAreaList}
              controllFunc={collectSelectedDistricts}
              selectedDistricts={selectedAreas}
              />
          </DropdownTooltip>
          <DropdownTooltip
            tooltipText="סטטוס"
          >
              <DropdownByIcon
                icon={
                  <ChecklistIcon />
                
              }
                isSelected={!!witchViewKav}
              >
                <KavFilter
                  id={witchViewKav}
                  setId={setWitchViewKav}
                />
              </DropdownByIcon>
          </DropdownTooltip>
          <DropdownTooltip
            tooltipText="הצג פניות"
          >
            <CircleActiveFilterBtn
              withAction
              func={handleFilterByProblems}
            >
              <ErrorIcon />
            </CircleActiveFilterBtn>
          </DropdownTooltip>
        {
          isLikutSortByKav ? (
            <DropdownTooltip
              tooltipText="סידור"
            >

              <DropdownByIcon
                icon={<SortIcon />}
                isSelected={isNeedToSortLikut === '1'}
                >
                <SortSelector 
                  defaultValue={isNeedToSortLikut}
                  onChange={handleChangeSort}
                  />
              </DropdownByIcon>
            </DropdownTooltip>

          ) : ''
        }
        {/* {
          isLikutSortByKav ? (
            <CircleActiveFilterBtn 
              func={handleChangeSort}
            >

              <SortIcon />
            </CircleActiveFilterBtn>

          ) : ''
        } */}
        <SearchInput input={searchInput} setInput={setSearchInput} />
        <NewKavSelector
          list={dataForAddKav}
          handleSelect={handleSelectNewKav}
        />
      </HeadWrapper>
      <Main>
        <DndContext
          sensors={sensors}
          collisionDetection={truckCollisionDetection}
          autoScroll={false}
          // Remeasure droppables on every drag tick. Default 'WhileDragging'
          // caches rects at drag start; if the kav ribbon has been scrolled
          // (or a new kav was inserted and shifted the layout) after that
          // snapshot, rects go stale and empty kavs stop accepting drops.
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
          onDragMove={onDragMove}
        >
          <Wrapper ref={kavRibbonRef}>
            <SortableContext
              id="general"
              items={render.filter(item => item.AreaID !== 0).map(item => `kav-${item.AreaID}`)}
              strategy={horizontalListSortingStrategy}
            >
              <Inner $dragged={isNeedToSortLikut === '1'}>
                {
                  render.filter(item => item.AreaID !== 0).map((item, i) => (
                    <SortableKavWrapper key={item.AreaID} areaID={item.AreaID}>
                      <KavItem
                        {...item}
                        index={i}
                        key={item.AreaID}
                        item={item}
                        carInfo={carsInfo?.dict}
                        changeDriver={changeDriver}
                        handleSelectToDrag={handleSelectToDrag}
                        driverVisible={driverVisible}
                        handleClickSelectOrder={handleClickSelectOrder}
                        handleOpenPackageOrder={handleOpenPackageOrder}
                        mergeHover={mergeHover}
                      />
                    </SortableKavWrapper>
                  ))
                }
              </Inner>
            </SortableContext>
          </Wrapper>
          {orderTree && orderTree[0] && (
            <ZeroColumn>
              <KavItem
                {...render.at(-1) || orderTree[0]}
                key={0}
                carInfo={carsInfo?.dict}
                changeDriver={changeDriver}
                handleSelectToDrag={handleSelectToDrag}
                handleClickSelectOrder={handleClickSelectOrder}
                reversed
                mergeHover={mergeHover}
              />
            </ZeroColumn>
          )}
          <DragOverlay dropAnimation={{ duration: 200, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
            {activeClient ? (
              <div style={{ width: 260, direction: 'rtl' }}>
                <ColumClientElem
                  {...activeClient.customer}
                  item={activeClient.customer}
                  index={0}
                  area={activeClient.area}
                  ordersAllowToShow={[4, 7, 9]}
                  handleSelectToDrag={() => {}}
                  handleClickSelectOrder={() => {}}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Main>
    </MainWrapper>
  );
};
const MainWrapper = styled.div`
  width: 100%;
  height: 100vh;
  padding: 0.5em;
  display: flex;
  flex-flow: column;
  gap: 0.5em;
  align-items: center;
  /* background: #eff3f4; */
  @media screen and (max-height: 500px) {
    position: relative;
    min-height: 1000px;
    overflow-y: scroll;
  }
`;
export const HeadWrapper = styled.div`
  width: 90%;
  min-height: 4em;
  background: #fff;
  border-radius: 30px;
  display: flex;
  align-items: center;
  padding: 0 0.4em;
  gap: 0.5em;
`;
const Main = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  height: calc(100% - 5em);
  background: #e9f3f2;
  gap: 1em;
  padding: 1em;
  border-radius: 30px;
  -webkit-box-shadow: 0px 1px 2px 2px rgba(161, 161, 161, 0.1);
  box-shadow: 0px 1px 2px 2px rgba(161, 161, 161, 0.1);
`;
const Wrapper = styled.div`
  max-height: 100%;
  width: max-content;
  overflow: auto;
  border-radius: 30px;
  direction: rtl;
  /* display: flex; */
  /* justify-content: flex-start; */
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;
const Inner = styled.div<{ $dragged?: boolean }>`
  min-width: 2000%;
  display: flex;
  flex-flow: row;
  gap: 1em;
  justify-content: flex-end;
  background: ${props => props.$dragged ? 'rgba(41,127,118, 0.5)' : ''};
  /* padding: 1em; */
  padding: 0 0.5em;
  flex-direction: row-reverse;
`;
const ZeroColumn = styled.div`
  display: flex;
  direction: rtl;
  justify-content: flex-start;
  max-height: 100vh;
  overflow-y: auto;
  min-width: 280px;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const WrapperKav = styled.div<{ $static?: boolean, $dragged?: boolean, $overwidth?: boolean, $isShowDriver?: boolean }>`
    min-width: ${props => props.$static ? '280px' : '280px'};
    background: ${props => props.$dragged ? 'rgba(41,127,118, 0.5)' : props.$isShowDriver ? "#80e081" : props.$overwidth ? 'rgba(255,99,71, 0.9)' : '#fff'} ;
    padding:  0.5em;
    position: relative;
    /* overflow: auto; */
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    align-items: center;
    gap: 1em;
    transition: 0.5s;
    /* top: 0; */
    right: 0;
    border-radius: 30px;
    
  

`
export default TrucksMain;
